/**
 * Anonymous rollout hash for this Connector — identical to Rust
 * `fleet_diagnostics::hash_machine_id`: "m" + first 16 hex of SHA-256(machine_id).
 * Sent as `machine_id_hash` so mycelium can place the machine in the staged-rollout
 * bucket (canary machines are offered the rollout tag, everyone else the fleet tag).
 * Returns null outside the Connector (plain browser) or on any failure.
 */
const MACHINE_ID_HASH_RE = /^m[0-9a-f]{16}$/;

export async function hashConnectorMachineId(machineId: string): Promise<string | null> {
  const id = String(machineId ?? '');
  const subtle = globalThis.crypto?.subtle;
  if (!id.trim() || !subtle) return null;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(id));
  const hex = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  return `m${hex.slice(0, 16)}`;
}

type TauriInvoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;

function tauriInvoke(): TauriInvoke | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { __TAURI__?: { core?: { invoke?: TauriInvoke }; invoke?: TauriInvoke } };
  const core = w.__TAURI__?.core || w.__TAURI__;
  return typeof core?.invoke === 'function' ? core.invoke.bind(core) : null;
}

let cached: Promise<string | null> | null = null;

export function getConnectorMachineIdHash(): Promise<string | null> {
  const invoke = tauriInvoke();
  if (!invoke) return Promise.resolve(null);
  if (!cached) {
    cached = (async () => {
      try {
        const hash = await hashConnectorMachineId(String((await invoke('get_machine_id')) ?? '').trim());
        return hash && MACHINE_ID_HASH_RE.test(hash) ? hash : null;
      } catch {
        return null;
      }
    })();
    void cached.then((h) => { if (!h) cached = null; });
  }
  return cached;
}

/** Test hook. */
export function resetConnectorMachineIdHashCache(): void {
  cached = null;
}
